/**
 * useResponsivePreview - Hook for managing responsive preview state
 *
 * Provides state and utilities for previewing pages at different breakpoints.
 * Integrates with the ComponentRenderer breakpoint prop.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { BREAKPOINTS, getBreakpoint, DESKTOP_CANVAS_WIDTH } from '../BreakpointToggle';

/**
 * Calculate the scaled dimensions for a container based on zoom level
 *
 * @param {number} originalWidth - Original width in pixels
 * @param {number} originalHeight - Original height in pixels (optional)
 * @param {number} zoomLevel - Zoom level as percentage (100 = 100%)
 * @returns {Object} - Scaled dimensions { width, height, scale }
 */
export function calculateScaledDimensions(originalWidth, originalHeight = null, zoomLevel = 100) {
  const scale = zoomLevel / 100;
  return {
    width: originalWidth * scale,
    height: originalHeight ? originalHeight * scale : null,
    scale,
  };
}

/**
 * useResponsivePreview - Manage responsive preview breakpoint state
 *
 * @param {Object} [options]
 * @param {string} [options.defaultBreakpoint='desktop'] - Initial breakpoint
 * @param {boolean} [options.persistKey] - LocalStorage key to persist selection
 * @param {Function} [options.onBreakpointChange] - Callback when breakpoint changes
 * @param {number} [options.zoomLevel=100] - Zoom level as percentage (100 = 100%)
 *
 * @returns {Object}
 * @returns {string} return.breakpoint - Current breakpoint ID
 * @returns {Function} return.setBreakpoint - Set breakpoint
 * @returns {number} return.previewWidth - Current preview width in pixels
 * @returns {Object} return.breakpointConfig - Full config for current breakpoint
 * @returns {Object} return.containerStyle - Styles for preview container (with zoom transform)
 * @returns {Object} return.wrapperStyle - Styles for outer wrapper
 * @returns {Object} return.zoomWrapperStyle - Styles for zoom wrapper (handles overflow/centering)
 * @returns {boolean} return.isMobile - True if current breakpoint is mobile (base)
 * @returns {boolean} return.isTablet - True if current breakpoint is tablet
 * @returns {boolean} return.isDesktop - True if current breakpoint is desktop
 * @returns {Object} return.scaledDimensions - Calculated scaled dimensions based on zoom
 */
export default function useResponsivePreview(options = {}) {
  const {
    defaultBreakpoint = 'base',
    persistKey = null,
    onBreakpointChange = null,
    zoomLevel = 100,
  } = options;

  // Initialize from localStorage if persistence is enabled
  const getInitialBreakpoint = () => {
    if (persistKey && typeof window !== 'undefined') {
      const stored = localStorage.getItem(persistKey);
      if (stored && BREAKPOINTS.find((bp) => bp.id === stored)) {
        return stored;
      }
    }
    return defaultBreakpoint;
  };

  const [breakpoint, setBreakpointInternal] = useState(getInitialBreakpoint);

  // Get current breakpoint config
  const breakpointConfig = useMemo(() => {
    return getBreakpoint(breakpoint) || BREAKPOINTS[BREAKPOINTS.length - 1];
  }, [breakpoint]);

  // Wrapped setter that handles persistence and callbacks
  const setBreakpoint = useCallback(
    (newBreakpoint) => {
      const validBreakpoint = BREAKPOINTS.find((bp) => bp.id === newBreakpoint);
      if (!validBreakpoint) {
        console.warn(`Invalid breakpoint: ${newBreakpoint}`);
        return;
      }

      setBreakpointInternal(newBreakpoint);

      // Persist to localStorage if enabled
      if (persistKey && typeof window !== 'undefined') {
        localStorage.setItem(persistKey, newBreakpoint);
      }

      // Fire callback
      if (onBreakpointChange) {
        onBreakpointChange(newBreakpoint, validBreakpoint);
      }
    },
    [persistKey, onBreakpointChange]
  );

  // Convenience breakpoint booleans
  const isMobile = breakpoint === 'base';
  const isTablet = breakpoint === 'tablet';
  const isDesktop = breakpoint === 'desktop';

  // Calculate scale factor from zoom level
  const scale = zoomLevel / 100;

  // Calculate scaled dimensions for the current breakpoint
  const scaledDimensions = useMemo(() => {
    return calculateScaledDimensions(breakpointConfig.width, null, zoomLevel);
  }, [breakpointConfig.width, zoomLevel]);

  // Container styles for preview sizing (includes zoom transform)
  // For desktop: use DESKTOP_CANVAS_WIDTH to simulate a real viewport
  // For tablet/mobile: use the breakpoint width
  const canvasWidth = breakpoint === 'desktop' ? DESKTOP_CANVAS_WIDTH : breakpointConfig.width;

  const containerStyle = useMemo(() => {
    const zoomStyles = {
      transform: `scale(${scale})`,
      transformOrigin: 'top center',
      transition: 'transform 0.15s ease-out',
    };

    // All breakpoints use fixed widths for accurate preview
    // Desktop uses DESKTOP_CANVAS_WIDTH to properly show max-width constraints
    return {
      width: `${canvasWidth}px`,
      maxWidth: `${canvasWidth}px`,
      margin: '0 auto',
      ...(breakpoint !== 'desktop' && {
        boxShadow: '0 0 0 1px rgba(0,0,0,0.1)',
        borderRadius: '8px',
        overflow: 'hidden',
      }),
      ...zoomStyles,
    };
  }, [breakpoint, canvasWidth, scale]);

  // Outer container styles (for centering the preview)
  const wrapperStyle = useMemo(() => {
    // All breakpoints now use centering to properly display the fixed-width canvas
    return {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      padding: breakpoint === 'desktop' ? '16px' : '16px',
      backgroundColor: breakpoint === 'desktop' ? '#f3f4f6' : '#f3f4f6',
      minHeight: '100%',
    };
  }, [breakpoint]);

  // Zoom wrapper styles - handles overflow and centering of scaled content
  const zoomWrapperStyle = useMemo(() => {
    const baseStyles = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      width: '100%',
      minHeight: '100%',
      transition: 'all 0.15s ease-out',
    };

    // When zoomed out (< 100%), content should still be centered
    if (zoomLevel < 100) {
      return {
        ...baseStyles,
        overflow: 'hidden',
      };
    }

    // When zoomed in (> 100%), enable scrolling
    if (zoomLevel > 100) {
      return {
        ...baseStyles,
        overflow: 'auto',
      };
    }

    // At 100%, normal behavior
    return {
      ...baseStyles,
      overflow: 'auto',
    };
  }, [zoomLevel]);

  return {
    // State
    breakpoint,
    setBreakpoint,

    // Derived values
    previewWidth: breakpointConfig.width,
    breakpointConfig,

    // Styles
    containerStyle,
    wrapperStyle,
    zoomWrapperStyle,

    // Zoom-related values
    zoomLevel,
    scaledDimensions,

    // Convenience booleans
    isMobile,
    isTablet,
    isDesktop,

    // All breakpoints (for custom rendering)
    allBreakpoints: BREAKPOINTS,
  };
}

/**
 * ResponsivePreviewContainer - Wrapper component for responsive preview
 *
 * Applies the container styles and centers the preview appropriately.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to render
 * @param {string} props.breakpoint - Current breakpoint
 * @param {string} [props.className] - Additional classes for outer container
 * @param {boolean} [props.showDeviceFrame=false] - Show device frame styling
 * @param {number} [props.zoomLevel=100] - Zoom level as percentage (100 = 100%)
 */
export function ResponsivePreviewContainer({
  children,
  breakpoint,
  className = '',
  showDeviceFrame = false,
  zoomLevel = 100,
}) {
  const {isMobile, isTablet } = useResponsivePreview({
    defaultBreakpoint: breakpoint,
  });

  // Sync internal state with prop
  useEffect(() => {
    // This is a controlled component - the parent manages the breakpoint
  }, [breakpoint]);

  // Compute styles based on breakpoint prop
  const config = getBreakpoint(breakpoint) || BREAKPOINTS[BREAKPOINTS.length - 1];
  const scale = zoomLevel / 100;

  // For desktop: use DESKTOP_CANVAS_WIDTH to simulate a real viewport
  // For tablet/mobile: use the breakpoint width
  const canvasWidth = breakpoint === 'desktop' ? DESKTOP_CANVAS_WIDTH : config.width;

  // Zoom transform styles
  const zoomStyles = {
    transform: `scale(${scale})`,
    transformOrigin: 'top center',
    transition: 'transform 0.15s ease-out',
  };

  // All breakpoints use fixed widths for accurate preview rendering
  const computedContainerStyle = {
    width: `${canvasWidth}px`,
    maxWidth: `${canvasWidth}px`,
    margin: '0 auto',
    ...(showDeviceFrame && breakpoint !== 'desktop' && {
      boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 4px 6px -1px rgba(0,0,0,0.1)',
      borderRadius: isMobile ? '24px' : isTablet ? '16px' : '8px',
      overflow: 'hidden',
      backgroundColor: 'white',
    }),
    ...zoomStyles,
  };

  // All breakpoints use centering to properly display the fixed-width canvas
  const computedWrapperStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '24px',
    backgroundColor: '#e5e7eb',
    minHeight: '100%',
  };

  // Zoom wrapper styles for handling overflow
  const computedZoomWrapperStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    width: '100%',
    minHeight: '100%',
    overflow: zoomLevel > 100 ? 'auto' : 'hidden',
    transition: 'all 0.15s ease-out',
  };

  return (
    <div className={`responsive-preview-wrapper ${className}`} style={computedWrapperStyle}>
      <div className="responsive-preview-zoom-wrapper" style={computedZoomWrapperStyle}>
        <div className="responsive-preview-container" style={computedContainerStyle}>
          {children}
        </div>
      </div>
    </div>
  );
}
